<template>
  <div class="neo4j-reports-slot">
    <!-- Setup form: pick a question and its filters, then load. -->
    <div class="neo4j-reports-slot__form p-2" v-if="!data.data.load">
      <b-form-group
        class="text-capitalize"
        :label="$t('grid.widgets_section.neo_dash.predefined_question')"
        label-for="neo4j-question"
      >
        <v-select
          id="neo4j-question"
          v-model="selectedQuestion"
          :options="questionOptions"
          :reduce="(opt) => opt.value"
          :clearable="false"
          :placeholder="$t('grid.widgets_section.neo_dash.predefined_question')"
          @input="applyQuestion"
        />
      </b-form-group>

      <b-form-group
        v-if="showCameras"
        class="text-capitalize"
        :label="$t('grid.widgets_section.neo_dash.cameras')"
        label-for="neo4j-cameras"
      >
        <v-select
          id="neo4j-cameras"
          v-model="data.data.cameras"
          :options="cameraOptions"
          :reduce="(opt) => opt.value"
          multiple
          :close-on-select="false"
          :loading="camerasLoading"
          :placeholder="$t('grid.widgets_section.neo_dash.cameras_placeholder')"
        />
      </b-form-group>

      <b-form-group
        v-if="isColorReport"
        class="text-capitalize"
        :label="$t('grid.widgets_section.neo_dash.colors')"
        label-for="neo4j-colors"
      >
        <v-select
          id="neo4j-colors"
          v-model="data.data.colors"
          :options="colorOptions"
          :reduce="(opt) => opt.value"
          multiple
          :close-on-select="false"
          :placeholder="$t('grid.widgets_section.neo_dash.colors_placeholder')"
        />
      </b-form-group>

      <b-form-group
        v-if="isPlateRoute"
        class="text-capitalize"
        :label="$t('grid.widgets_section.neo_dash.report.plate')"
        label-for="neo4j-plate"
      >
        <b-form-input
          id="neo4j-plate"
          :value="data.data.plate"
          :placeholder="$t('grid.widgets_section.neo_dash.plate_route_placeholder')"
          @update="(v) => (data.data.plate = (v || '').toUpperCase())"
        />
      </b-form-group>

      <b-form-group
        v-if="isOverSpeed"
        class="text-capitalize"
        :label="$t('grid.widgets_section.neo_dash.min_speed_label')"
        label-for="neo4j-min-speed"
      >
        <b-form-input
          id="neo4j-min-speed"
          type="number"
          min="0"
          step="5"
          :value="data.data.min_speed"
          :placeholder="$t('grid.widgets_section.neo_dash.min_speed_placeholder')"
          @update="(v) => (data.data.min_speed = v === '' ? 50 : Number(v))"
        />
      </b-form-group>

      <b-form-group
        v-if="isConvoy"
        class="text-capitalize"
        :label="$t('grid.widgets_section.neo_dash.window_seconds_label')"
        label-for="neo4j-window-seconds"
      >
        <b-form-input
          id="neo4j-window-seconds"
          type="number"
          min="1"
          max="300"
          step="1"
          :value="data.data.window_seconds"
          :placeholder="$t('grid.widgets_section.neo_dash.window_seconds_placeholder')"
          @update="(v) => (data.data.window_seconds = v === '' ? 10 : Number(v))"
        />
      </b-form-group>

      <b-form-group
        v-if="isReport && hasDaysFilter"
        class="text-capitalize"
        :label="$t('grid.widgets_section.neo_dash.time_range')"
        label-for="neo4j-days"
      >
        <v-select
          id="neo4j-days"
          v-model="data.data.days"
          :options="daysOptions"
          :reduce="(opt) => opt.value"
          :clearable="false"
        />
      </b-form-group>

      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        class="mt-1 text-capitalize"
        type="button"
        :disabled="!canLoad"
        @click="data.data.load = true"
      >
        {{ $t("actions.load") }}
      </b-button>
    </div>

    <!-- Result: native dashboard, trajectory graph or report charts. -->
    <div v-else class="neo4j-reports-slot__frame">
      <div class="neo4j-reports-slot__toolbar">
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="flat-secondary"
          size="sm"
          @click="data.data.load = false"
        >
          ‹ {{ $t("actions.go_back") }}
        </b-button>
      </div>
      <div :key="refreshKey" class="neo4j-reports-slot__content">
        <lpr-analytics-dashboard v-if="isDashboard" :cameras="data.data.cameras" />
        <neo4j-report-view
          v-else
          :question="data.data.question"
          :days="data.data.days"
          :colors="data.data.colors"
          :cameras="data.data.cameras"
          :plate="data.data.plate"
          :min-speed="data.data.min_speed"
          :window-seconds="data.data.window_seconds"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { BFormGroup, BButton, BFormInput } from "bootstrap-vue";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import axios from "@axios";
import { REPORT_COLORS } from "./vehicleColors";

// Report question keys, mirror the API whitelist (graph-report endpoint).
const REPORT_QUESTIONS = [
  "cars_by_day",
  "cars_by_color",
  "cars_by_hour",
  "cars_by_camera",
  "vehicle_types",
  "vehicle_brands",
  "top_plates",
  "plates_registered",
  "camera_flow",
  "plate_clones",
  "plate_route",
  "over_speed",
  "exposure_time",
  "repeat_rate",
  "new_vs_repeat_hour",
  "convoy",
];

export default {
  components: {
    BFormGroup,
    BButton,
    BFormInput,
    vSelect,
    LprAnalyticsDashboard: () => import("./LprAnalyticsDashboard.vue"),
    Neo4jReportView: () => import("./Neo4jReportView.vue"),
  },
  directives: {
    Ripple,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedQuestion: null,
      cameraOptions: [],
      camerasLoading: false,
      refreshKey: 0,
    };
  },
  computed: {
    questionOptions() {
      return [
        { label: this.$t("grid.widgets_section.neo_dash.dashboard.title"), value: "dashboard" },
        ...REPORT_QUESTIONS.map((key) => ({
          label: this.$t(`grid.widgets_section.neo_dash.questions.${key}`),
          value: key,
        })),
      ];
    },
    colorOptions() {
      return REPORT_COLORS.map((key) => ({
        label: this.$t(`grid.widgets_section.neo_dash.color_names.${key}`),
        value: key,
      }));
    },
    daysOptions() {
      const lastDays = (n) => ({
        label: this.$t("grid.widgets_section.neo_dash.last_days", { n }),
        value: n,
      });
      return [
        lastDays(3),
        lastDays(7),
        lastDays(30),
        lastDays(90),
        { label: this.$t("grid.widgets_section.neo_dash.all_time"), value: 0 },
      ];
    },
    isDashboard() {
      return this.data.data.mode === "dashboard";
    },
    isReport() {
      return this.data.data.mode === "report";
    },
    isColorReport() {
      return this.isReport && this.data.data.question === "cars_by_color";
    },
    isPlateRoute() {
      return this.isReport && this.data.data.question === "plate_route";
    },
    isOverSpeed() {
      return this.isReport && this.data.data.question === "over_speed";
    },
    isConvoy() {
      return this.isReport && this.data.data.question === "convoy";
    },
    hasDaysFilter() {
      return this.data.data.question !== "plates_registered";
    },
    hasCameras() {
      return Array.isArray(this.data.data.cameras) && this.data.data.cameras.length > 0;
    },
    hasColors() {
      return Array.isArray(this.data.data.colors) && this.data.data.colors.length > 0;
    },
    hasPlate() {
      return !!(this.data.data.plate && this.data.data.plate.trim());
    },
    showCameras() {
      return this.isDashboard || (this.isReport && this.data.data.question !== "plates_registered");
    },
    requiresCamera() {
      if (this.isDashboard) return true;
      if (!this.isReport) return false;
      return !["plates_registered", "cars_by_camera", "plate_route"].includes(this.data.data.question);
    },
    canLoad() {
      if (!this.isDashboard && !this.isReport) return false;
      if (this.isColorReport && !this.hasColors) return false;
      if (this.isPlateRoute && !this.hasPlate) return false;
      if (this.requiresCamera && !this.hasCameras) return false;
      return this.isDashboard || !!this.data.data.question;
    },
  },
  methods: {
    applyQuestion(value) {
      if (value === "dashboard") {
        this.$set(this.data.data, "mode", "dashboard");
        return;
      }
      this.$set(this.data.data, "mode", "report");
      this.$set(this.data.data, "question", value);
    },
    loadCameras() {
      this.camerasLoading = true;
      axios
        .get("/v1/detection_event/graph-cameras")
        .then(({ data }) => {
          const list = (data && data.data) || [];
          this.cameraOptions = list.map((c) => ({
            label: c.name ? c.name : `Cámara ${c.id}`,
            value: String(c.id),
          }));
        })
        .catch(() => {
          this.cameraOptions = [];
        })
        .finally(() => {
          this.camerasLoading = false;
        });
    },
    refreshPage() {
      this.refreshKey++;
    },
  },
  mounted() {
    if (!Array.isArray(this.data.data.cameras)) {
      this.$set(this.data.data, "cameras", []);
    }
    if (!Array.isArray(this.data.data.colors) || !this.data.data.colors.length) {
      this.$set(this.data.data, "colors", ["red", "green"]);
    }
    if (typeof this.data.data.days !== "number") {
      this.$set(this.data.data, "days", 7);
    }
    if (typeof this.data.data.plate !== "string") {
      this.$set(this.data.data, "plate", "");
    }
    if (typeof this.data.data.min_speed !== "number") {
      this.$set(this.data.data, "min_speed", 50);
    }
    if (typeof this.data.data.window_seconds !== "number") {
      this.$set(this.data.data, "window_seconds", 10);
    }

    ["baseUrl", "database", "reportType", "query", "neo4jProtocol", "neo4jUser",
      "neo4jPassword", "neo4jHost", "neo4jPort"].forEach((key) => {
      if (key in this.data.data) this.$delete(this.data.data, key);
    });
    if (!["dashboard", "report"].includes(this.data.data.mode)) {
      this.$set(this.data.data, "mode", null);
      this.data.data.load = false;
    }

    this.selectedQuestion = this.isReport ? this.data.data.question : this.data.data.mode;
    this.loadCameras();
  },
};
</script>

<style lang="scss" scoped>
.neo4j-reports-slot {
  width: 100%;
  height: 100%;
}

.neo4j-reports-slot__form {
  width: 100%;
  height: 100%;
  overflow-y: auto;
}

.neo4j-reports-slot__frame {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.neo4j-reports-slot__toolbar {
  flex: 0 0 auto;
  padding: 4px 6px;
}

.neo4j-reports-slot__content {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  z-index: 1;
}
</style>
